import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ResponsiveContainer,
} from 'recharts';
import {
  ShieldCheck,
  Download,
  Cpu,
  GitBranch,
  Hash,
  ScanLine,
  Lock,
  ArrowRight,
  Search,
  AlertTriangle,
  CheckCircle2,
  Layers,
  Loader2,
} from 'lucide-react';
import { getProofScore, getProofScoreAudit } from '../services/api';

/* ----------------------------------------------------------------------------
 * Screen C — Recruiter "Results & Audit" view for a proof-of-skill work sample.
 * Neutral palette + a single signal-green accent reserved for "verified".
 * The score shown is computed in our backend (not by the model); this view is
 * purely an explainable, audit-ready presentation of that record.
 * -------------------------------------------------------------------------- */

const C = {
  ink: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate400: '#94A3B8',
  hairline: '#E2E8F0',
  page: '#F8FAFC',
  surface: '#FFFFFF',
  // signal green = "verified"
  green: '#2563EB',
  greenInk: '#1D4FD8',
  greenSoft: '#EFF4FE',
  greenLine: '#BFDBFE',
  // amber = "partial / needs review"
  amber: '#B45309',
  amberSoft: '#FFF8EB',
  amberLine: '#F4E2BE',
  track: '#EEF2F6',
};

const serif = "'Source Serif 4', Georgia, 'Times New Roman', serif";

// A realistic sample record (same shape as GET /api/proof/scores/:id) so the
// screen renders standalone. A real :id in the URL replaces this with live data.
const SAMPLE_SCORE = {
  id: 'sample',
  normalized_score: 100,
  raw_points_awarded: 10,
  raw_points_possible: 10,
  threshold_applied: 60,
  outcome: 'advance',
  injection_flag: false,
  self_consistency_n: 1,
  scoring_method: 'llm',
  model_id: 'claude-haiku-4-5',
  model_version: 'claude-haiku-4-5',
  rubric_version: 1,
  prompt_version: 1,
  created_at: '2026-06-24T15:20:00.000Z',
  work_sample_title: 'Design an API rate limiter',
  role_family: 'Backend engineering',
  candidate_label: 'Avery Quinn',
  per_criterion: [
    {
      id: 'correctness',
      verdict: 'MET',
      points_awarded: 10,
      points_possible: 10,
      weight: 0.5,
      evidence_quote:
        "I'd use a token bucket. Each client gets a bucket with a capacity (burst size) and a refill rate… Thread-safety comes from the lock.",
      explanation:
        'Correct token-bucket design that is thread-safe and handles bursts, with a distributed Redis variant for shared state.',
    },
    {
      id: 'clarity',
      verdict: 'MET',
      points_awarded: 10,
      points_possible: 10,
      weight: 0.3,
      evidence_quote:
        'class TokenBucket: … def allow(self): with self.lock: … self.tokens = min(self.capacity, self.tokens + elapsed * refill_rate)',
      explanation:
        'Clear, well-structured pseudocode with a readable explanation of each moving part.',
    },
    {
      id: 'tradeoffs',
      verdict: 'MET',
      points_awarded: 10,
      points_possible: 10,
      weight: 0.2,
      evidence_quote:
        'token bucket allows controlled bursts vs a fixed window which is simpler but has boundary spikes… move the counter to Redis with an atomic INCR + Lua script.',
      explanation:
        'Discusses multiple concrete tradeoffs: burst handling, memory O(active clients), and distributed state.',
    },
  ],
  overall_explanation:
    'A correct, well-explained token-bucket rate limiter with concrete tradeoffs and a distributed-systems extension.',
};

const VERDICTS = {
  MET: { bg: C.greenSoft, color: C.greenInk, border: C.greenLine, label: 'Met' },
  PARTIAL: { bg: C.amberSoft, color: C.amber, border: C.amberLine, label: 'Partial' },
  UNMET: { bg: '#F8FAFC', color: C.slate500, border: C.hairline, label: 'Unmet' },
};
const verdictStyle = (v) => VERDICTS[v] || { bg: '#F8FAFC', color: C.slate500, border: C.hairline, label: v || '—' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return iso; }
};
const prettyId = (id) => (id || '').replace(/[_-]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

/* ---------- small presentational pieces ---------- */

function Eyebrow({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
      color: C.green, display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green }} />
      {children}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 12,
      boxShadow: '0 1px 2px rgba(15,23,42,0.04)', ...style,
    }}>
      {children}
    </div>
  );
}

function ScoreRing({ score, outcome }) {
  const band = score >= 80 ? C.green : score >= 60 ? '#CA8A04' : C.slate400;
  const data = [{ value: Math.max(0, Math.min(100, score)) }];
  return (
    <div style={{ position: 'relative', width: 196, height: 196 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          innerRadius="76%" outerRadius="100%" data={data}
          startAngle={90} endAngle={-270} barSize={16}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar
            background={{ fill: C.track }} dataKey="value" cornerRadius={16}
            angleAxisId={0} fill={band} isAnimationActive
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      }}>
        <div style={{ fontFamily: serif, fontSize: 52, fontWeight: 600, lineHeight: 1, color: C.ink }}>
          {Math.round(score)}
        </div>
        <div style={{ fontSize: 13, color: C.slate500, marginTop: 2 }}>out of 100</div>
        <div style={{
          marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12.5, fontWeight: 600,
          color: outcome === 'advance' ? C.greenInk : C.slate500,
        }}>
          <ShieldCheck size={14} strokeWidth={2.2} />
          {outcome === 'advance' ? 'Verified' : 'Below bar'}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ icon: Icon, label, value, mono }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: `1px solid ${C.hairline}` }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 13, color: C.slate500 }}>
        <Icon size={15} strokeWidth={1.8} style={{ color: C.slate400 }} />
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 500, color: C.ink, fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit' }}>
        {value}
      </span>
    </div>
  );
}

function CriterionRow({ c }) {
  const vs = verdictStyle(c.verdict);
  const pct = c.points_possible > 0 ? Math.round((c.points_awarded / c.points_possible) * 100) : 0;
  const barColor = c.verdict === 'MET' ? C.green : c.verdict === 'PARTIAL' ? '#CA8A04' : C.slate400;
  return (
    <div style={{ padding: '20px 22px', borderBottom: `1px solid ${C.hairline}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, color: C.ink }}>{prettyId(c.id)}</span>
          <span style={{
            fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
            background: vs.bg, color: vs.color, border: `1px solid ${vs.border}`,
          }}>{vs.label}</span>
          {typeof c.weight === 'number' && (
            <span style={{ fontSize: 11.5, color: C.slate400 }}>weight {c.weight}</span>
          )}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap' }}>
          {c.points_awarded}<span style={{ color: C.slate400, fontWeight: 400 }}> / {c.points_possible} pts</span>
        </span>
      </div>

      {/* points bar */}
      <div style={{ height: 8, borderRadius: 6, background: C.track, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 6, transition: 'width .5s ease' }} />
      </div>

      {c.evidence_quote && (
        <blockquote style={{
          margin: '0 0 8px', padding: '8px 14px', borderLeft: `3px solid ${C.greenLine}`,
          background: '#FCFDFE', borderRadius: '0 6px 6px 0', color: C.slate700,
          fontSize: 13.5, lineHeight: 1.55, fontStyle: 'italic',
        }}>
          “{c.evidence_quote}”
        </blockquote>
      )}
      {c.explanation && (
        <p style={{ margin: 0, fontSize: 13.5, color: C.slate500, lineHeight: 1.55 }}>{c.explanation}</p>
      )}
    </div>
  );
}

/* ---------- page ---------- */

function ProofScore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isSample = !id || id === 'sample';

  const [score, setScore] = useState(isSample ? SAMPLE_SCORE : null);
  const [loading, setLoading] = useState(!isSample);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [lookupId, setLookupId] = useState('');

  useEffect(() => {
    let active = true;
    if (isSample) { setScore(SAMPLE_SCORE); setLoading(false); setError(null); return; }
    setLoading(true); setError(null);
    getProofScore(id)
      .then((res) => { if (active) { setScore(res.data); setLoading(false); } })
      .catch((e) => {
        if (!active) return;
        setError(e.response?.data?.error || 'Could not load this result.');
        setLoading(false);
      });
    return () => { active = false; };
  }, [id, isSample]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      let auditObj;
      if (isSample) {
        auditObj = {
          decision_id: '00000000-0000-0000-0000-0000000000sample'.slice(0, 36),
          proof_score_id: 'sample',
          work_sample_id: '6b453d08-d71d-4f64-87bf-23de98c7cc72',
          assessment_version: SAMPLE_SCORE.rubric_version,
          model_id: SAMPLE_SCORE.model_id,
          model_version: SAMPLE_SCORE.model_version,
          rubric_version: SAMPLE_SCORE.rubric_version,
          prompt_version: SAMPLE_SCORE.prompt_version,
          scoring_method: SAMPLE_SCORE.scoring_method,
          overall_score: SAMPLE_SCORE.normalized_score,
          normalized_score: SAMPLE_SCORE.normalized_score,
          threshold_applied: SAMPLE_SCORE.threshold_applied,
          outcome: SAMPLE_SCORE.outcome,
          per_criterion: SAMPLE_SCORE.per_criterion,
          input_hash: 'sample-deadbeefsample-deadbeefsample-deadbeef',
          row_hash: 'sample-cafebabesample-cafebabesample-cafebabe',
          human_override: false,
          created_by: 'proofScoringService',
          note: 'Illustrative sample audit record — not a real scored submission.',
        };
      } else {
        const res = await getProofScoreAudit(id);
        auditObj = res.data;
      }
      const blob = new Blob([JSON.stringify(auditObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-${id || 'sample'}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Audit record exported');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not export audit record');
    } finally {
      setExporting(false);
    }
  }, [id, isSample]);

  const goLookup = (e) => {
    e.preventDefault();
    const v = lookupId.trim();
    if (v) navigate(`/proof/scores/${v}`);
  };

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', color: C.ink }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <Eyebrow>Proof of skill · Verified result</Eyebrow>
          <h1 style={{ fontFamily: serif, fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', margin: '12px 0 6px' }}>
            Work-sample result
          </h1>
          <p style={{ fontSize: 14, color: C.slate500, margin: 0 }}>
            {score?.work_sample_title || 'Scored submission'}
            {score?.candidate_label ? ` · ${score.candidate_label}` : ''}
            {score?.created_at ? ` · ${fmtDate(score.created_at)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isSample && (
            <span style={{
              fontSize: 12, fontWeight: 600, color: C.slate500, background: C.page,
              border: `1px solid ${C.hairline}`, borderRadius: 20, padding: '5px 12px',
            }}>Sample result</span>
          )}
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600,
              color: C.greenInk, background: C.greenSoft, border: `1px solid ${C.greenLine}`,
              borderRadius: 8, padding: '9px 15px', cursor: exporting || loading ? 'default' : 'pointer',
              opacity: exporting || loading ? 0.6 : 1,
            }}
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} strokeWidth={2} />}
            Export audit record
          </button>
        </div>
      </div>

      {loading && (
        <Card style={{ padding: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: C.slate500 }}>
          <Loader2 size={18} className="animate-spin" /> Loading result…
        </Card>
      )}

      {!loading && error && (
        <Card style={{ padding: 32 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AlertTriangle size={20} style={{ color: C.amber, flex: 'none', marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{error}</div>
              <div style={{ fontSize: 13.5, color: C.slate500 }}>
                Check the result ID, or{' '}
                <button onClick={() => navigate('/proof')} style={{ color: C.green, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  view the sample result
                </button>.
              </div>
            </div>
          </div>
        </Card>
      )}

      {!loading && !error && score && (
        <>
          {/* top: score + provenance */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 20, marginBottom: 20 }} className="proof-top-grid">
            {/* score panel */}
            <Card style={{ padding: 26 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
                <ScoreRing score={score.normalized_score} outcome={score.outcome} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600,
                    padding: '5px 12px', borderRadius: 20, marginBottom: 14,
                    background: score.outcome === 'advance' ? C.greenSoft : C.amberSoft,
                    color: score.outcome === 'advance' ? C.greenInk : C.amber,
                    border: `1px solid ${score.outcome === 'advance' ? C.greenLine : C.amberLine}`,
                  }}>
                    {score.outcome === 'advance'
                      ? <><CheckCircle2 size={14} /> Advance — meets the bar</>
                      : <><AlertTriangle size={14} /> Below the {score.threshold_applied}-point bar</>}
                  </div>
                  <p style={{ fontSize: 14, color: C.slate700, lineHeight: 1.6, margin: '0 0 14px' }}>
                    {score.overall_explanation || 'Scored against the role rubric.'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: C.slate500 }}>
                    <Lock size={13} style={{ color: C.slate400 }} />
                    Score computed in code from per-criterion points — not asserted by the model.
                  </div>
                </div>
              </div>
            </Card>

            {/* provenance panel */}
            <Card style={{ padding: '20px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Layers size={16} style={{ color: C.slate500 }} />
                <h3 style={{ fontFamily: serif, fontSize: 15.5, fontWeight: 600, margin: 0 }}>Provenance</h3>
              </div>
              <MetaRow icon={Cpu} label="Model" value={score.model_id || '—'} mono />
              <MetaRow icon={GitBranch} label="Rubric version" value={`v${score.rubric_version ?? '—'}`} />
              <MetaRow icon={GitBranch} label="Prompt version" value={`v${score.prompt_version ?? '—'}`} />
              <MetaRow icon={ScanLine} label="Self-consistency" value={`${score.self_consistency_n ?? 1}× sample`} />
              <MetaRow icon={Hash} label="Threshold" value={`${score.threshold_applied ?? 60} pts`} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0 2px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 13, color: C.slate500 }}>
                  <ShieldCheck size={15} strokeWidth={1.8} style={{ color: C.slate400 }} /> Injection scan
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                  background: score.injection_flag ? C.amberSoft : C.greenSoft,
                  color: score.injection_flag ? C.amber : C.greenInk,
                  border: `1px solid ${score.injection_flag ? C.amberLine : C.greenLine}`,
                }}>
                  {score.injection_flag ? 'Flagged for review' : 'Clean'}
                </span>
              </div>
            </Card>
          </div>

          {/* per-criterion explainability */}
          <Card style={{ overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: `1px solid ${C.hairline}` }}>
              <h3 style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, margin: 0 }}>How the score was reached</h3>
              <span style={{ fontSize: 12.5, color: C.slate500 }}>
                {score.raw_points_awarded ?? '—'} / {score.raw_points_possible ?? '—'} weighted points
              </span>
            </div>
            {(score.per_criterion || []).map((c, i) => <CriterionRow key={c.id || i} c={c} />)}
            {(!score.per_criterion || score.per_criterion.length === 0) && (
              <div style={{ padding: 24, fontSize: 13.5, color: C.slate500 }}>No per-criterion breakdown on this record.</div>
            )}
          </Card>

          {/* audit / fairness footer */}
          <Card style={{ padding: '20px 22px', marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, maxWidth: 620 }}>
                <ShieldCheck size={20} style={{ color: C.green, flex: 'none', marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Logged immutably for audit</div>
                  <p style={{ fontSize: 13.5, color: C.slate500, lineHeight: 1.6, margin: 0 }}>
                    Every score writes one append-only audit row snapshotting the model, rubric &amp; prompt versions,
                    a hash of the candidate&apos;s input, and the per-criterion decision. Scored blind — no protected
                    attributes are in the scoring path. Export the full record for an EEOC / LL-144 / EU&nbsp;AI&nbsp;Act trail.
                  </p>
                </div>
              </div>
              <button
                onClick={handleExport}
                disabled={exporting}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600,
                  color: C.ink, background: C.surface, border: `1px solid ${C.hairline}`,
                  borderRadius: 8, padding: '9px 15px', cursor: exporting ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}
              >
                <Download size={15} strokeWidth={2} /> Download audit.json
              </button>
            </div>
          </Card>
        </>
      )}

      {/* lookup another result (only on the index/sample view) */}
      {isSample && !loading && (
        <Card style={{ padding: '18px 22px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.slate700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={15} style={{ color: C.slate400 }} /> Look up a result by ID
          </div>
          <form onSubmit={goLookup} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="proof_scores id (UUID)"
              style={{
                flex: 1, minWidth: 240, height: 40, padding: '0 14px', fontSize: 13.5,
                border: `1px solid ${C.hairline}`, borderRadius: 8, color: C.ink, background: C.page,
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 600,
                color: '#fff', background: C.green, border: `1px solid ${C.green}`,
                borderRadius: 8, padding: '0 16px', height: 40, cursor: 'pointer',
              }}
            >
              View result <ArrowRight size={15} strokeWidth={2.2} />
            </button>
          </form>
        </Card>
      )}

      <style>{`
        @media (max-width: 880px) {
          .proof-top-grid { grid-template-columns: 1fr !important; }
        }
        .animate-spin { animation: proofspin 1s linear infinite; }
        @keyframes proofspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default ProofScore;
